// 1. React imports
import { useEffect } from 'react';

// 2. External libraries (TanStack Router)
import { useNavigate } from '@tanstack/react-router';

// 4. Internal components
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { LoadingSpinner } from '~/components/common/LoadingSpinner';

interface CallbackError {
  title: string;
  details: string;
}

export interface OAuthInstallCallbackProps {
  /** Provider name shown to the user, e.g. "Slack". */
  provider: string;
  /** Provider-specific error codes; `access_denied`, `invalid_state`, and `topic_not_found` are shared. */
  errors: Record<string, CallbackError>;
  connectionId?: string;
  topicId?: string;
  /** The `error` search param the backend sets on failure. */
  errorCode?: string;
  /** The `message` search param with backend error details. */
  errorMessage?: string;
}

/**
 * Landing screen for a chat-app OAuth install redirect (Slack, Discord).
 * On success it forwards to the new connection; on failure it explains what
 * happened and offers a retry that reopens the create-connection dialog.
 */
export function OAuthInstallCallback({
  provider,
  errors,
  connectionId,
  topicId,
  errorCode,
  errorMessage,
}: OAuthInstallCallbackProps) {
  const navigate = useNavigate();

  // Neither the error branch below nor a valid redirect is possible without
  // both identifiers; treat that as a terminal error instead of spinning
  // forever, since these search params never arrive after the initial render.
  const isMissingParams = !errorCode && (!connectionId || !topicId);

  useEffect(() => {
    if (errorCode || isMissingParams) {
      return;
    }

    void navigate({
      to: `/topics/${topicId}/connections/${connectionId}`,
      state: { oauthComplete: true },
    });
  }, [connectionId, topicId, errorCode, isMissingParams, navigate]);

  if (errorCode || isMissingParams) {
    const errorMessages: Record<string, CallbackError> = {
      invalid_state: {
        title: 'OAuth session expired',
        details: 'Please try creating the connection again.',
      },
      topic_not_found: {
        title: 'Topic no longer exists',
        details: 'The topic was deleted during installation.',
      },
      ...errors,
    };

    const error = errorCode
      ? (errorMessages[errorCode] ?? {
          title: 'Connection failed',
          details: errorMessage || 'An unknown error occurred during OAuth.',
        })
      : {
          title: `${provider} connection failed`,
          details: 'The installation redirect was missing required details. Please try again.',
        };

    return (
      <div className="mx-auto max-w-[600px] px-4">
        <div className="mt-16">
          <ErrorAlert
            message={error.title}
            details={error.details}
            onRetry={() => {
              void navigate({
                to: topicId ? `/topics/${topicId}` : '/',
                state: { reopenConnectionDialog: topicId ? true : undefined },
              });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[600px] px-4">
      <LoadingSpinner message={`Completing ${provider} installation...`} />
    </div>
  );
}
